import { LayoutDashboard, Sparkles, ShieldAlert, Ticket, MonitorSmartphone, Cloud, Receipt, BookText, DatabaseBackup, Radar, Network, AtSign, type LucideIcon } from 'lucide-react';
import { ROUTES } from '@/lib/routes';

/**
 * The Cosmos module registry — the single source of truth for what the platform
 * covers. The sidebar (`components/shell/Sidebar`), Home and the placeholder pages
 * (`ModulePlaceholder`) read from here, so adding a module is one entry plus a
 * route.
 *
 * When a module goes live: flip `status` to 'live', point `to` at its real
 * landing route, and drop `planned`.
 */
export interface ModuleDef {
  id: 'dashboards' | 'ai' | 'insights' | 'psa' | 'soc' | 'rmm' | 'cipp' | 'csp' | 'network' | 'domains' | 'docs' | 'backup';
  /** Nav label — kept short (sidebar row, rail tooltip, Home card). */
  label: string;
  /** The system behind the module, shown on the placeholder page. */
  vendor: string;
  icon: LucideIcon;
  status: 'live' | 'planned';
  to: string;
  blurb: string;
  /** What this module is intended to do. Only meaningful while `planned`. */
  planned?: string[];
}

export const MODULES: ModuleDef[] = [
  {
    id: 'dashboards',
    label: 'Dashboards',
    vendor: 'Your own, from every module',
    icon: LayoutDashboard,
    status: 'live',
    to: ROUTES.dashboards,
    blurb: 'Build, save and share dashboards from any Cosmos metric; full screen for wall displays.',
  },
  {
    id: 'ai',
    label: 'AI',
    vendor: 'Microsoft Foundry agent · Synfo MCP',
    icon: Sparkles,
    status: 'live',
    to: ROUTES.ai,
    blurb: 'Ask questions across Autotask, Datto RMM and IT Glue in one chat. Read-only.',
  },
  {
    id: 'insights',
    label: 'Insights',
    vendor: 'Across every module',
    icon: Radar,
    status: 'live',
    to: ROUTES.insights.coverage,
    blurb: 'Coverage gaps with tickets, check-in drift and one page per client.',
  },
  {
    id: 'psa',
    label: 'PSA',
    vendor: 'Autotask PSA',
    icon: Ticket,
    status: 'live',
    to: ROUTES.psa.root,
    blurb: 'Tickets, opportunities, time and notes.',
  },
  {
    id: 'rmm',
    label: 'RMM',
    vendor: 'Datto RMM',
    icon: MonitorSmartphone,
    status: 'live',
    to: ROUTES.rmm.devices,
    blurb: 'Device health, alerts and remote actions.',
  },
  {
    id: 'soc',
    label: 'SOC',
    vendor: 'Huntress (managed EDR / ITDR / SIEM)',
    icon: ShieldAlert,
    status: 'live',
    to: ROUTES.soc.root,
    blurb: 'Huntress incidents, agents and identities, joined to Datto RMM and Autotask.',
  },
  {
    id: 'cipp',
    label: 'CIPP',
    vendor: 'CIPP — Microsoft 365 multi-tenant management',
    icon: Cloud,
    status: 'live',
    to: ROUTES.cipp.tenants,
    blurb: 'Microsoft 365 tenants, users, MFA and security posture.',
  },
  {
    id: 'csp',
    label: 'CSP',
    vendor: 'Microsoft CSP — NCE licences (Partner Center + Autotask)',
    icon: Receipt,
    status: 'live',
    to: ROUTES.csp.dashboard,
    blurb: 'NCE renewals, licence requests and ordering.',
  },
  {
    id: 'network',
    label: 'Network',
    vendor: 'Sophos firewalls · UniFi',
    icon: Network,
    status: 'live',
    to: ROUTES.network.root,
    blurb: 'Firewalls, subscriptions, UniFi sites and devices.',
  },
  {
    id: 'domains',
    label: 'Domains',
    vendor: 'dmarcian · CIPP DNS checks',
    icon: AtSign,
    status: 'live',
    to: ROUTES.domains.root,
    blurb: 'DMARC enforcement journey, senders and unmonitored domains.',
  },
  {
    id: 'docs',
    label: 'Docs',
    vendor: 'IT Glue',
    icon: BookText,
    status: 'live',
    to: ROUTES.docs.organizations,
    blurb: 'Client documentation, configurations and expirations — in context on tickets and devices.',
  },
  {
    id: 'backup',
    label: 'Backup',
    vendor: 'Datto SaaS Protection (BCDR later)',
    icon: DatabaseBackup,
    status: 'live',
    to: ROUTES.backup.saas,
    blurb: 'Microsoft 365 backup health and coverage gaps.',
  },
];

export const getModule = (id: ModuleDef['id']) => MODULES.find((m) => m.id === id)!;

/** Modules that are not built yet — the ones rendering a placeholder page. */
export const PLANNED_MODULES = MODULES.filter((m) => m.status === 'planned');
